import { useMemo } from 'react';
import { useStorage } from '@funtech-int/web-toolkit';
import { useFromBlock, useFeatureFlags, useAuthManager } from '@funtech-int/yango-shared';
import { useSignal } from '@funtech-int/rival-react';
import { UAParser } from 'ua-parser-js';

import { getDeviceUuid } from '@/shared/lib/deviceUuid';
import { useCurrentUrl } from '@/shared/hooks/useCurrentUrl';
import { useProfileManager } from '@/shared/hooks/useProfileManager';
import { clientConfig } from '@/config/client';

import { featureFlagsScheme } from '../lib/featureFlags';
import { PlayerTrackerData } from '../lib/playerTracker';

import { usePlayback } from './usePlayback';

const { appName, appVersion, productServiceName: configServiceName } = clientConfig;

// 1 - desktop browser for Mediascope
const DEVICE_TYPE = 1;

function noNull<T>(value: T | null | undefined): T | undefined {
    return value === null ? undefined : value;
}

const DEFAULT_OS_VERSION = '0';
const DEFAULT_DEVICE_VENDOR = 'Unknown';
const DEFAULT_DEVICE_MODEL = 'Unknown';

function getBrowserMinorVersion(browserVersion?: string): string | undefined {
    if (!browserVersion) {
        return browserVersion;
    }

    return browserVersion.substring(browserVersion.indexOf('.') + 1);
}

interface Browser extends IUAParser.IBrowser {
    minor?: string;
}

interface DeviceInfo {
    browser?: Browser;
    operationSystem?: string;
    operationSystemMajorVersion: string;
    operationSystemMinorVersion: string;
    deviceVendor: string;
    deviceModel: string;
}

function getDeviceInfo(): DeviceInfo {
    const parser = new UAParser();

    const currentBrowser = parser.getBrowser();
    const currentOS = parser.getOS();
    const [operationSystemMajorVersion, ...operationSystemRestVersions] =
        currentOS.version?.split('.') || [];

    const device = parser.getDevice();

    return {
        browser: {
            ...currentBrowser,
            minor: getBrowserMinorVersion(currentBrowser.version),
        },
        operationSystem: currentOS.name,
        operationSystemMajorVersion: operationSystemMajorVersion || DEFAULT_OS_VERSION,
        operationSystemMinorVersion: operationSystemRestVersions?.join('.') || DEFAULT_OS_VERSION,
        deviceVendor: device.vendor ?? DEFAULT_DEVICE_VENDOR,
        deviceModel: device.model ?? DEFAULT_DEVICE_MODEL,
    };
}

const TRACKER_DEVICE_INFO = getDeviceInfo();

export function useTrackerData(): PlayerTrackerData {
    const playback = usePlayback();
    const profileManager = useProfileManager();
    const localPuid = profileManager.userProfile?.puid;
    const { testIds } = useFeatureFlags(featureFlagsScheme);
    const storage = useStorage();
    const authManager = useAuthManager();

    const {
        sessionIds: { globalSessionId, localSessionId: localSessionIdSignal },
    } = useFromBlock();

    const localSessionId = useSignal(localSessionIdSignal);
    const currentUrl = useCurrentUrl().toString();

    return useMemo(() => {
        const { kpId, puid, serviceName, ...restStreamTrackingData } =
            playback.trackingData.streamTrackingData;

        const streamTrackingDataWithDefaults = {
            kpId: kpId?.toString(),
            puid: (puid ?? noNull(localPuid))?.toString(),
            serviceName: serviceName ?? configServiceName,
            ...restStreamTrackingData,
        };
        const deviceUuid = getDeviceUuid(storage);

        const {
            browser,
            operationSystem,
            operationSystemMajorVersion,
            operationSystemMinorVersion,
        } = TRACKER_DEVICE_INFO;

        return {
            deviceId: '-',
            platformVersion: '-',
            platformModel: '-',
            platformVendor: '-',
            platformVendorDeviceId: '-',
            appName,
            appVersion,
            platformOs: operationSystem,
            platformOsVersionMajor: operationSystemMajorVersion,
            platformOsVersionMinor: operationSystemMinorVersion,
            browserName: browser?.name,
            browserVer: browser?.version,
            browserVerMajor: browser?.major,
            browserVerMinor: browser?.minor,
            deviceType: DEVICE_TYPE,
            psid: playback.psid,
            prerollUuid: 'preroll',
            deviceUuid,
            prepareStartupDataTime: Date.now(),
            yandexuid: authManager.yandexuid,
            subProfileId: undefined,
            page: encodeURIComponent(currentUrl),
            testIds: testIds.join(','),
            globalSessionId,
            localSessionId,
            ...streamTrackingDataWithDefaults,
        };
    }, [
        playback.trackingData.streamTrackingData,
        playback.psid,
        localPuid,
        storage,
        authManager.yandexuid,
        currentUrl,
        testIds,
        globalSessionId,
        localSessionId,
    ]);
}
